import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, ArrowLeft, Clock } from 'lucide-react';
import api from '../../../../services/api';
import LoadingSpinner from '../../../common/LoadingSpinner';
import { getUserIdentifier, normalizeUserProfiles } from '../../../../utils/userProfile';

const EmployeeAttendanceCalendar = ({ basePath = '/admin/attendance' }) => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [approvedWfh, setApprovedWfh] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Company-wide attendance start date (days before this are "not applicable")
  const COMPANY_ATTENDANCE_START = new Date(2026, 3, 27, 0, 0, 0, 0); // 2026-04-27 (local)

  const startOfLocalDayClient = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(x.getTime())) return null;
    return new Date(x.getFullYear(), x.getMonth(), x.getDate(), 0, 0, 0, 0);
  };

  const getAttendanceEligibilityStartDate = () => {
    // Attendance should not be counted before the company start date.
    let start = COMPANY_ATTENDANCE_START;
    if (employee && employee.onboardingStatus === 'approved') {
      if (employee.onboardingApprovedAt) {
        start = startOfLocalDayClient(employee.onboardingApprovedAt) || start;
      } else if (employee.joinDate) {
        start = startOfLocalDayClient(employee.joinDate) || start;
      }
    }
    if (!start) return null;
    // Use the later of (company start) and (employee eligibility start)
    return start.getTime() < COMPANY_ATTENDANCE_START.getTime() ? COMPANY_ATTENDANCE_START : start;
  };

  const isBeforeAttendanceEligibility = (date) => {
    if (!date) return false;
    const start = getAttendanceEligibilityStartDate();
    if (!start) return false;
    return startOfLocalDayClient(date).getTime() < start.getTime();
  };

  useEffect(() => {
    fetchEmployeeData();
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    if (employee) {
      fetchMonthAttendance();
      fetchApprovedLeaves();
      fetchApprovedWfh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, employee]);

  const fetchEmployeeData = async () => {
    try {
      const response = await api.get('/admin/employees');
      const employees = normalizeUserProfiles(response.data.employees || []);
      const emp = employees.find((e) => getUserIdentifier(e) === employeeId);
      setEmployee(emp);
    } catch (error) {
      console.error('Error fetching employee:', error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await api.get('/admin/holidays');
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const isHoliday = (date) => {
    if (!date) return null;
    return holidays.find((h) => {
      const holidayDate = new Date(h.date);
      return (
        holidayDate.getFullYear() === date.getFullYear() &&
        holidayDate.getMonth() === date.getMonth() &&
        holidayDate.getDate() === date.getDate()
      );
    });
  };

  const isSunday = (date) => {
    if (!date) return false;
    return date.getDay() === 0;
  };

  // Recurring rule: the 2nd and 4th Saturday of every month are system week-offs for
  // HO (and anyone with no branch assigned). SSbranch employees work every Saturday -
  // no alternate week-off - matching utils/payrollCalculator.js on the backend.
  const isAltSaturdayHoliday = (date) => {
    if (!date) return false;
    if (employee?.branch === 'SSbranch') return false;
    if (date.getDay() !== 6) return false;
    return Math.ceil(date.getDate() / 7) % 2 === 0;
  };

  const fetchMonthAttendance = async () => {
    try {
      setLoading(true);
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await api.get(`/admin/attendance?month=${month}&year=${year}&userId=${employeeId}`);
      
      // Group attendance by date
      const grouped = {};
      response.data.attendances.forEach(att => {
        const attDate = new Date(att.date);
        const dateKey = `${attDate.getFullYear()}-${String(attDate.getMonth() + 1).padStart(2, '0')}-${String(attDate.getDate()).padStart(2, '0')}`;
        grouped[dateKey] = att;
      });
      
      setAttendanceData(grouped);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedLeaves = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await api.get(`/admin/employee/${employeeId}/approved-leaves?month=${month}&year=${year}`);
      setApprovedLeaves(response.data.leaves || []);
    } catch (error) {
      console.error('Error fetching approved leaves:', error);
    }
  };

  const fetchApprovedWfh = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await api.get(`/admin/employee/${employeeId}/approved-wfh?month=${month}&year=${year}`);
      setApprovedWfh(response.data.wfhRequests || []);
    } catch (error) {
      console.error('Error fetching approved WFH:', error);
    }
  };

  const getLeaveForDate = (date) => {
    if (!date) return null;
    if (isBeforeAttendanceEligibility(date)) return null;

    for (const leave of approvedLeaves) {
      const leaveDay = leave.leaveDays?.find(day => {
        const dayDate = new Date(day.date);
        return dayDate.getFullYear() === date.getFullYear() &&
               dayDate.getMonth() === date.getMonth() &&
               dayDate.getDate() === date.getDate();
      });
      
      if (leaveDay) {
        return leaveDay;
      }
    }
    
    return null;
  };

  const getWfhForDate = (date) => {
    if (!date) return null;
    if (isBeforeAttendanceEligibility(date)) return null;

    for (const wfh of approvedWfh) {
      const wfhDay = wfh.wfhDays?.find(day => {
        const dayDate = new Date(day.date);
        return dayDate.getFullYear() === date.getFullYear() &&
               dayDate.getMonth() === date.getMonth() &&
               dayDate.getDate() === date.getDate();
      });
      if (wfhDay) return wfhDay;
    }
    return null;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getDateAttendance = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;
    return attendanceData[dateKey];
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isFutureDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'full-day':
        return 'bg-gradient-to-br from-green-50 to-green-100 border-green-200';
      case 'half-day':
        return 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200';
      case 'absent':
        return 'bg-gradient-to-br from-red-50 to-red-100 border-red-200';
      default:
        return 'bg-gray-200 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'full-day':
        return '✓';
      case 'half-day':
        return '½';
      case 'absent':
        return '✗';
      default:
        return '';
    }
  };

  // Monthly stats are derived by walking the same days-in-month grid with the
  // same precedence the calendar cells use (eligibility/future skipped, then
  // holiday/Sunday, then leave, then WFH, then punched attendance) so the
  // tiles below the calendar always agree with what's actually drawn in it.
  const monthlyStats = getDaysInMonth(currentDate)
    .filter(Boolean)
    .reduce((acc, date) => {
      const beforeActive = isBeforeAttendanceEligibility(date);
      const future = !beforeActive && isFutureDate(date);
      if (beforeActive || future) return acc;

      const holidayInfo = isHoliday(date);
      // 2nd/4th Saturday is folded into `sunday` itself - same auto full-paid
      // treatment, so the stats stay in lockstep with the calendar grid below.
      const sunday = isSunday(date) || isAltSaturdayHoliday(date);
      if (holidayInfo || sunday) return acc;

      const leaveDay = getLeaveForDate(date);
      if (leaveDay) {
        const units = leaveDay.dayType === 'half' ? 0.5 : 1;
        if (leaveDay.leaveType === 'earned') acc.earnedLeaves += units;
        else if (leaveDay.leaveType === 'sick') acc.sickLeaves += units;
        else if (leaveDay.leaveType === 'comp_off') acc.compOffLeaves += units;
        else if (leaveDay.leaveType === 'without_pay') acc.withoutPayLeaves += units;
        return acc;
      }

      const wfhDay = getWfhForDate(date);
      if (wfhDay) return acc;

      const attendance = getDateAttendance(date);
      if (attendance) {
        if (attendance.status === 'full-day') acc.fullDay += 1;
        else if (attendance.status === 'half-day') acc.halfDay += 1;
        else if (attendance.status === 'absent') acc.absent += 1;
        acc.totalHours += attendance.totalHours || 0;
      }
      return acc;
    }, { fullDay: 0, halfDay: 0, absent: 0, totalHours: 0, earnedLeaves: 0, sickLeaves: 0, compOffLeaves: 0, withoutPayLeaves: 0 });

  const days = getDaysInMonth(currentDate);

  if (loading && !employee) {
    return <LoadingSpinner size="lg" className="mt-8" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl shadow-xl p-8 text-white">
        <button
          onClick={() => navigate(`${basePath}/employee-wise`)}
          className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white text-indigo-700 border border-indigo-100 shadow-md hover:shadow-lg hover:bg-indigo-50 transition-all duration-200 mb-5 group"
          aria-label="Back to Employee List"
        >
          <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" />
          <span className="font-medium">Back to Employee List</span>
        </button>
        
        {employee && (
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 rounded-full bg-white bg-opacity-25 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
              {employee.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold">{employee.name}</h1>
              <p className="text-indigo-50 mt-1">{employee.email}</p>
              <div className="flex items-center space-x-4 mt-2">
                <span className="text-sm bg-indigo-400 bg-opacity-40 px-3 py-1 rounded-full">
                  ID: {employee.employeeId || 'N/A'}
                </span>
                <span className="text-sm bg-indigo-400 bg-opacity-40 px-3 py-1 rounded-full">
                  {employee.organization || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calendar Card */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Month Navigation */}
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePreviousMonth}
              className="p-2 hover:bg-indigo-400 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
            
            <div className="flex items-center space-x-3">
              <CalendarIcon className="h-6 w-6 text-white" />
              <h2 className="text-2xl font-bold text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
            </div>
            
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-indigo-400 rounded-lg transition-colors"
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-6 overflow-x-auto md:overflow-visible">
          <div className="min-w-[900px] md:min-w-0">
              {loading ? (
                <LoadingSpinner size="lg" className="py-8" />
              ) : (
                <>
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {dayNames.map((day) => (
                      <div
                        key={day}
                        className="text-center font-semibold text-gray-600 text-sm py-2"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-2">
                    {days.map((date, index) => {
                      const beforeActive = date && isBeforeAttendanceEligibility(date);
                      const attendance = beforeActive ? null : getDateAttendance(date);
                      const today = isToday(date);
                      const future = date && !beforeActive && isFutureDate(date);
                      const holidayInfo = beforeActive ? null : isHoliday(date);
                      // 2nd/4th Saturday folded into `sunday` itself - renders and
                      // behaves identically: auto full-paid, excluded from attendance.
                      const sunday = beforeActive ? false : (isSunday(date) || isAltSaturdayHoliday(date));
                      const leaveDay = getLeaveForDate(date);
                      const wfhDay = !leaveDay ? getWfhForDate(date) : null;

                      return (
                        <div
                          key={index}
                          title={beforeActive ? 'Employee not active yet' : undefined}
                          className={`
                            min-h-24 p-3 min-w-[120px] rounded-xl border-2 transition-all duration-200
                            ${!date ? 'bg-gray-50 border-transparent cursor-default' : ''}
                            ${beforeActive ? 'bg-gray-100 border-gray-300 border-dashed cursor-not-allowed opacity-80' : ''}
                            ${future ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50' : ''}
                            ${today && !future ? `border-indigo-500 ${beforeActive ? 'bg-gray-100' : 'bg-indigo-50'}` : ''}
                            ${holidayInfo && !today && !future ? 'bg-sky-100 border-sky-300' : ''}
                            ${sunday && !holidayInfo && !today && !future ? 'bg-gradient-to-br from-green-100 to-green-200 border-green-400 border-2 shadow-md' : ''}
                            ${leaveDay && !today && !future && !holidayInfo && !sunday ? (
                              leaveDay.leaveType === 'earned'
                                ? (leaveDay.dayType === 'half' ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-300' : 'bg-gradient-to-br from-green-100 to-green-200 border-green-400')
                                : leaveDay.leaveType === 'sick'
                                ? (leaveDay.dayType === 'half' ? 'bg-gradient-to-br from-pink-50 to-pink-100 border-pink-300' : 'bg-gradient-to-br from-pink-100 to-pink-200 border-pink-400')
                                : leaveDay.leaveType === 'comp_off'
                                ? (leaveDay.dayType === 'half' ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-300' : 'bg-gradient-to-br from-amber-100 to-amber-200 border-amber-400')
                                : leaveDay.leaveType === 'casual'
                                ? (leaveDay.dayType === 'half' ? 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-300' : 'bg-gradient-to-br from-purple-100 to-purple-200 border-purple-400')
                                : (leaveDay.dayType === 'half' ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300' : 'bg-gradient-to-br from-orange-100 to-orange-200 border-orange-400')
                            ) + ' border-2 shadow-md' : ''}
                            ${wfhDay && !today && !future && !holidayInfo && !sunday ? (
                              wfhDay.dayType === 'half'
                                ? 'bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-300'
                                : 'bg-gradient-to-br from-cyan-100 to-cyan-200 border-cyan-400'
                            ) + ' border-2 shadow-md' : ''}
                            ${attendance && !today && !future && !holidayInfo && !sunday && !leaveDay && !wfhDay ? `${getStatusColor(attendance.status)} border-2 shadow-md` : ''}
                            ${!attendance && date && !today && !future && !holidayInfo && !sunday && !leaveDay && !wfhDay && !beforeActive ? 'bg-white border-gray-200 hover:bg-gray-50' : ''}
                          `}
                        >
                          {date && (
                            <div className="h-full flex flex-col">
                              <div className="flex items-center justify-between mb-2">
                                <span className={`
                                  text-lg font-bold
                                  ${today ? 'text-indigo-700' : ''}
                                  ${beforeActive && !today ? 'text-gray-500' : ''}
                                  ${holidayInfo && !today ? 'text-sky-700' : ''}
                                  ${sunday && !holidayInfo && !today ? 'text-green-700' : ''}
                                  ${leaveDay && !today && !holidayInfo && !sunday ? (leaveDay.leaveType === 'earned' ? 'text-green-700' : leaveDay.leaveType === 'sick' ? 'text-pink-700' : leaveDay.leaveType === 'comp_off' ? 'text-amber-700' : leaveDay.leaveType === 'casual' ? 'text-purple-700' : 'text-orange-700') : ''}
                                  ${wfhDay && !today && !holidayInfo && !sunday ? 'text-cyan-700' : ''}
                                  ${attendance && !today && !holidayInfo && !sunday && !leaveDay && !wfhDay ? 'text-gray-700' : ''}
                                  ${!attendance && !today && !holidayInfo && !sunday && !future && !leaveDay && !wfhDay && !beforeActive ? 'text-gray-700' : ''}
                                  ${future ? 'text-gray-400' : ''}
                                `}>
                                  {date.getDate()}
                                </span>
                                {today && (
                                  <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                                    Today
                                  </span>
                                )}
                                {sunday && !holidayInfo && !leaveDay && !wfhDay && !future && (
                                  <span className="text-xl font-bold text-green-600">✓</span>
                                )}
                                 {holidayInfo && !leaveDay && !wfhDay && !future && (
                                  <span className="text-xl font-bold text-sky-600">✓</span>
                                )}
                                {attendance && !leaveDay && !wfhDay && !sunday && (
                                  <span className={`text-xl font-bold ${
                                    attendance.status === 'full-day' ? 'text-green-600' :
                                    attendance.status === 'half-day' ? 'text-yellow-600' :
                                    'text-red-600'
                                  }`}>
                                    {getStatusIcon(attendance.status)}
                                  </span>
                                )}
                              </div>

                              {/* Holiday Badge — holidays are paid full days */}
                              {holidayInfo && !future && (
                                <div className="mb-2 bg-sky-200 border border-sky-400 rounded px-2 py-1">
                                  <div className="text-xs font-semibold text-sky-900 text-center">
                                    {holidayInfo.description}
                                  </div>
                                  <div className="text-xs font-semibold text-sky-900 text-center">
                                    Full Day
                                  </div>
                                </div>
                              )}

                              

                              {/* Sunday = Full Day */}
                              {sunday && !holidayInfo && !future && (
                                <div className="mb-2 bg-green-200 border border-green-400 rounded px-2 py-1">
                                  <div className="text-xs font-semibold text-green-900 text-center">
                                    Full Day
                                  </div>
                                </div>
                              )}

                              {/* Leave Badge */}
                              {leaveDay && !future && (
                                <div className={`mb-2 rounded px-2 py-1 border ${
                                  leaveDay.leaveType === 'earned'
                                    ? 'bg-green-200 border-green-400'
                                    : leaveDay.leaveType === 'sick'
                                    ? 'bg-pink-200 border-pink-400'
                                    : leaveDay.leaveType === 'comp_off'
                                    ? 'bg-amber-200 border-amber-400'
                                    : leaveDay.leaveType === 'casual'
                                    ? 'bg-purple-200 border-purple-400'
                                    : 'bg-orange-200 border-orange-400'
                                }`}>
                                  <div className={`text-xs font-semibold text-center ${
                                    leaveDay.leaveType === 'earned' ? 'text-green-900' : leaveDay.leaveType === 'sick' ? 'text-pink-900' : leaveDay.leaveType === 'comp_off' ? 'text-amber-900' : leaveDay.leaveType === 'casual' ? 'text-purple-900' : 'text-orange-900'
                                  }`}>
                                    {leaveDay.dayType === 'half' ? 'Half ' : 'Full '}
                                    {leaveDay.leaveType === 'earned' ? 'Earned' : leaveDay.leaveType === 'sick' ? 'Sick' : leaveDay.leaveType === 'comp_off' ? 'Comp Off' : leaveDay.leaveType === 'casual' ? 'Casual' : 'W/O Pay'} Leave
                                  </div>
                                </div>
                              )}

                              {wfhDay && !leaveDay && !future && (
                                <div className="mb-2 rounded px-2 py-1 border bg-cyan-200 border-cyan-400">
                                  <div className="text-xs font-semibold text-center text-cyan-900">
                                    {wfhDay.dayType === 'half' ? 'Half ' : 'Full '}WFH (Present)
                                  </div>
                                </div>
                              )}

                              {attendance && !future && !leaveDay && !wfhDay && (
                                <div className="space-y-1 text-xs">
                                  <div className="bg-white rounded px-2 py-1">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">In</span>
                                      <span className="font-bold text-gray-900">
                                        {new Date(attendance.checkInTime).toLocaleTimeString('en-US', {
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                  {attendance.checkOutTime && (
                                    <div className="bg-white rounded px-2 py-1">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Out</span>
                                        <span className="font-bold text-gray-900">
                                          {new Date(attendance.checkOutTime).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  {attendance.totalHours > 0 && (
                                    <div className="bg-white rounded px-2 py-1">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Hours</span>
                                        <span className="font-bold text-gray-900">
                                          {attendance.totalHours.toFixed(1)}h
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {sunday && !holidayInfo && !future && !leaveDay && !wfhDay && !beforeActive && !attendance && (
                                <div className="flex-1 flex flex-col items-center justify-center space-y-1">
                                  <span className="text-2xl font-bold text-green-600">✓</span>
                                  <span className="text-xs font-semibold text-green-700">Full Day</span>
                                </div>
                              )}
                              
                              {holidayInfo && !future && !leaveDay && !wfhDay && !beforeActive && !attendance && (
                                <div className="flex-1 flex flex-col items-center justify-center space-y-1">
                                  <span className="text-2xl font-bold text-sky-600">✓</span>
                                  <span className="text-xs font-semibold text-sky-700">Full Day</span>
                                </div>
                              )}

                              {!attendance && !future && !holidayInfo && !sunday && !leaveDay && !wfhDay && !beforeActive && (
                                <div className="flex-1 flex flex-col items-center justify-center space-y-2">
                                  <span className="text-2xl font-bold text-red-600">✗</span>
                                  <span className="text-xs font-semibold text-red-600">Absent</span>
                                </div>
                              )}

                              {beforeActive && (
                                <div className="flex-1 flex items-center justify-center">
                                  <span className="text-xs text-gray-500">—</span>
                                </div>
                              )}

                              {future && (
                                <div className="flex-1 flex items-center justify-center">
                                  <span className="text-xs text-gray-400">Future</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="font-semibold text-gray-700">Legend:</span>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200"></div>
              <span className="text-gray-600">Full Day</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-200"></div>
              <span className="text-gray-600">Half Day</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200"></div>
              <span className="text-gray-600">Absent</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-sky-100 border-2 border-sky-300"></div>
              <span className="text-gray-600">Holiday</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-600"></span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-indigo-50 border-2 border-indigo-500"></div>
              <span className="text-gray-600">Today</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gray-100 border-2 border-dashed border-gray-300"></div>
              <span className="text-gray-600">Not active yet</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-green-100 to-green-200 border-2 border-green-400"></div>
              <span className="text-gray-600">Full Earned Leave</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300"></div>
              <span className="text-gray-600">Half Earned Leave</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-pink-100 to-pink-200 border-2 border-pink-400"></div>
              <span className="text-gray-600">Full Sick Leave</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-pink-50 to-pink-100 border-2 border-pink-300"></div>
              <span className="text-gray-600">Half Sick Leave</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-purple-100 to-purple-200 border-2 border-purple-400"></div>
              <span className="text-gray-600">Full Casual Leave</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300"></div>
              <span className="text-gray-600">Half Casual Leave</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-orange-100 to-orange-200 border-2 border-orange-400"></div>
              <span className="text-gray-600">Full W/O Pay Leave</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300"></div>
              <span className="text-gray-600">Half W/O Pay Leave</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-cyan-100 to-cyan-200 border-2 border-cyan-400"></div>
              <span className="text-gray-600">WFH (Present)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Full Days</p>
              <p className="text-3xl font-bold mt-1">{monthlyStats.fullDay}</p>
            </div>
            <div className="bg-green-400 bg-opacity-30 rounded-full p-3">
              <span className="text-2xl">✓</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Half Days</p>
              <p className="text-3xl font-bold mt-1">{monthlyStats.halfDay}</p>
            </div>
            <div className="bg-yellow-400 bg-opacity-30 rounded-full p-3">
              <span className="text-2xl">½</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Absents</p>
              <p className="text-3xl font-bold mt-1">{monthlyStats.absent}</p>
            </div>
            <div className="bg-red-400 bg-opacity-30 rounded-full p-3">
              <span className="text-2xl">✗</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Hours</p>
              <p className="text-3xl font-bold mt-1">{monthlyStats.totalHours.toFixed(1)}</p>
            </div>
            <div className="bg-blue-400 bg-opacity-30 rounded-full p-3">
              <Clock className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Leave Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm font-medium">Earned Leaves</p>
              <p className="text-3xl font-bold mt-1">{monthlyStats.earnedLeaves}</p>
            </div>
            <div className="bg-emerald-400 bg-opacity-30 rounded-full p-3">
              <span className="text-2xl">🌴</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-pink-100 text-sm font-medium">Sick Leaves</p>
              <p className="text-3xl font-bold mt-1">{monthlyStats.sickLeaves}</p>
            </div>
            <div className="bg-pink-400 bg-opacity-30 rounded-full p-3">
              <span className="text-2xl">🏥</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm font-medium">Comp Off Leaves</p>
              <p className="text-3xl font-bold mt-1">{monthlyStats.compOffLeaves}</p>
            </div>
            <div className="bg-amber-400 bg-opacity-30 rounded-full p-3">
              <span className="text-2xl">🎁</span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">W/O Pay Leaves</p>
              <p className="text-3xl font-bold mt-1">{monthlyStats.withoutPayLeaves}</p>
            </div>
            <div className="bg-orange-400 bg-opacity-30 rounded-full p-3">
              <span className="text-2xl">💼</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Info */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Monthly Summary for {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <p className="text-gray-700">
              {employee?.name} has recorded <span className="font-bold text-green-600">{monthlyStats.fullDay} full days</span>, 
              <span className="font-bold text-yellow-600"> {monthlyStats.halfDay} half days</span>, and 
              <span className="font-bold text-red-600"> {monthlyStats.absent} absents</span> with a total of 
              <span className="font-bold text-blue-600"> {monthlyStats.totalHours.toFixed(1)} hours</span> worked this month.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendanceCalendar;
